import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import clsx from 'clsx'
import useSWR from 'swr'

import { PressFeedDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'

import { onImageError } from '@/utils/sentry/onImageError'

import { Button } from '@/components/Button'
import { Link } from '@/components/CustomLink'
import { Image } from '@/components/Image'

import { serializeCacheKey } from '../../../../../lib/contentful/utils'
import { ContentfulImage } from '../../../common/ContentfulImage'
import { Pagination } from '../../Marketplace/Pagination'

type Props = {
	className?: string
	hideImage?: boolean
	type: 'News' | 'Press Release'
	ctaText?: string
}

export const limit = 10

export function PressFeed({ className, hideImage, type, ctaText }: Props) {
	const { query, locale, isPreview } = useRouter()

	const skip =
		query?.page && typeof query.page === 'string' ? (parseInt(query.page) - 1) * limit : 0

	const { data } = useSWR(serializeCacheKey('pressFeed', { limit, skip, type, locale }), () =>
		client({ preview: isPreview }).request(PressFeedDocument, {
			where: { type_in: [type, 'News / Press Release'], enabled: true },
			limit,
			skip,
			locale,
			preview: isPreview
		})
	)
	const [total, setTotal] = useState(data?.pressCollection?.total ?? 0)

	useEffect(() => {
		if (!data || !data.pressCollection) return

		setTotal(data.pressCollection.total)
	}, [data])

	return (
		<div className={clsx(className, 'grid gap-14')} data-testid="press-feed">
			{data?.pressCollection?.items.map(
				(item) =>
					item?.title && (
						<div
							key={item.sys.id}
							data-testid="press-feed-item"
							className="grid sm:grid-cols-4 gap-8"
						>
							{!hideImage &&
								((item.image?.__typename === 'AssetInternal' && item.image.media) ||
									(item.image?.__typename === 'AssetExternal' && item.image?.url)) && (
									<div className="px-4 relative items-start justify-center flex">
										{item.image?.__typename === 'AssetInternal' && item.image.media ? (
											<ContentfulImage className="w-full" field={{ data: item.image.media }} />
										) : (
											item.image?.__typename === 'AssetExternal' && (
												<Image
													className="w-full"
													src={`https:${item.image?.url}`}
													alt={`Image for ${item.title}`}
													layout="fill"
													onError={onImageError}
												/>
											)
										)}
									</div>
								)}
							<div className="sm:col-span-3">
								{item.publishedDate && (
									<p className="font-medium text-xxs text-bc-black uppercase pb-3">
										{new Date(item.publishedDate).toLocaleDateString('en-US', {
											day: 'numeric',
											month: 'long',
											year: 'numeric'
										})}
									</p>
								)}

								<h4 className="mb-2 font-medium text-2xl leading-[33px] text-bc-black hover:text-bc-blue">
									<Link
										data-testid="press-feed-title"
										href={item.externalLink?.url ?? `/press/releases/${item.slug}`}
										target={item.externalLink?.url ? '_blank' : '_self'}
									>
										{item.title}
									</Link>
								</h4>

								<p className="font-normal text-[15px] leading-[27px] text-gray-600">
									{item.summary}
								</p>

								<div className="grid grid-flow-row gap-3">
									<Button
										variant="subtle"
										color="secondary"
										size="large"
										link={{ href: item.externalLink?.url ?? `/press/releases/${item.slug}` }}
										showIcon={true}
										className="mt-7 w-fit"
									>
										{ctaText || 'Continue Reading'}
									</Button>
								</div>
							</div>
						</div>
					)
			)}

			<div className="place-self-start">
				<Pagination total={total} limit={limit} />
			</div>
		</div>
	)
}
